import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, BehaviorSubject, of, forkJoin, lastValueFrom } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { Profile, Role } from '../models/index';
import { CONTEXT_PERMISSIONS_ENTRY, ROLES } from './assets/constants';
import { HttpDataService } from './http-data.service';
import { UtilsService } from './utils.service';
import { UiService } from './ui.service';
import { JournalService } from './journal.service';
import { JwtService } from './jwt.service';
import { ConfigurationsService } from './configurations.service';
import { AuthService } from 'app/auth/auth.service';
import navigationMenu from '../assets/navigation_menu.json';
import { ContextService } from './context/context.service';
import { AgentsService } from './agents.service';
import { ClientsService } from './clients.service';

@Injectable()
export class ProfileService extends HttpDataService {
  public loggedUser: Profile;
  public loggedUserSubject = new BehaviorSubject(null);
  public loggedUserRole = new BehaviorSubject(null);
  public userRoles: Role[];
  private _permissions = {};

  constructor(
    protected injector: Injector,
    public router: Router,
    public authService: AuthService,
    public agentsService: AgentsService,
    public clientsService: ClientsService,
    public translateService: TranslateService,
    public uiService: UiService,
    public journalService: JournalService,
    public jwtService: JwtService,
    public configurationsService: ConfigurationsService,
    private contextService: ContextService
  ) {
    super(injector, '/admins');
    this.jwtService
      .getToken$()
      .pipe(distinctUntilChanged())
      .subscribe(() => this.reset());
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const credentials = route.queryParams['credentials'];
    const isProfileLoaded = !!this.loggedUser;

    const validToken = await this.validateToken(credentials);
    if (!validToken) {
      return false;
    }

    const user = await lastValueFrom(this.loadUser());

    const role = user.roleOptions;

    await this.contextService.setContext(route, this.getFixedClientId());

    if (!isProfileLoaded) {
      this.loggedUserSubject.next(this.loggedUser);
      this.setLoggedUserRole();
    }

    if (credentials) {
      const credentialsData = await this.authService.verifyToken(credentials);
      if (credentialsData.email !== user.email) {
        this.uiService.setLoginEnvironment(credentials, state.url);
      }
      if (role.scope === 'client' && credentialsData.clientId && credentialsData.clientId !== user.selectedClientId) {
        if (!user.clientIds.includes(credentialsData.clientId)) {
          this.router.navigate(['/invalid-credentials']).then(); // non-existent route this shows not found component
          return false;
        }
        await this.changeSelectedClient(credentialsData.clientId, state.url);
      }
    }

    if (this.uiService.getLandingUrl()) {
      this.uiService.setLandingUrl();
      return false;
    }

    if (state.url === '/home' && user.role !== 'superAdmin') {
      if (role.scope === 'client' || ['supportSpecialist', 'csm', 'csAdmin'].includes(role.name)) {
        this.router.navigate(['/dashboard']).then();
      } else if (role.name === 'expert' || role.name === 'distributor') {
        this.router.navigate(['/clients']).then();
      } else {
        this.router.navigate(['/agents', user.id]).then();
      }
      return false;
    }

    return this.canExecute(route.data['action']);
  }

  resolve(): Observable<Profile> {
    return this.loadUser();
  }

  reset() {
    this.loggedUser = null as any;
    this.loggedUserSubject.next(null);
    this.loggedUserRole.next(null);
  }

  get permissions() {
    return this._permissions;
  }

  set permissions(permissions) {
    this.contextService.setContextPermissionsConfig(permissions[CONTEXT_PERMISSIONS_ENTRY]);
    this._permissions = permissions;
  }

  public getLoggedUser$(): Observable<Profile> {
    return this.loggedUserSubject.asObservable();
  }

  public getLoggedUserRole$(): Observable<any> {
    return this.loggedUserRole.asObservable();
  }

  public getLoggedUser(): Profile {
    return this.loggedUser;
  }

  public getLoggedUserClientType(): string {
    return this.loggedUser.clientType;
  }

  public getLoggedUserRole(): any {
    return this.loggedUser.roleOptions;
  }

  setLoggedUserRole() {
    if (this.loggedUser && this.userRoles) {
      if (this.loggedUser.roleOptions.isAgent) {
        this.loggedUserRole.next(this.loggedUser.roleOptions);
      } else {
        this.reset();
        this.uiService.setLoginEnvironment();
      }
    }
  }

  loadUser(): Observable<Profile> {
    // See main.ts
    const debugLang = sessionStorage.getItem('debug_lang');
    const rolesUrl = `${this.baseUrl}/roles`;
    const profileUrl = `${this.baseUrl}/profile`;

    if (this.loggedUser) {
      return of(this.loggedUser);
    }

    // eslint-disable-next-line
    return this.http.get(profileUrl, { headers: { 'x-service-id': environment.SERVICE_ID } }).pipe(
      switchMap((profile: Profile) =>
        forkJoin({
          profile: of(profile),
          roles: this.http.get(rolesUrl),
          permissions: this.configurationsService.getConfigurationProfile({
            sid: environment.SERVICE_ID
          })
        })
      ),
      map(({ profile, roles, permissions }) => {
        this.loggedUser = profile;
        this.userRoles = roles as any[];
        this.permissions = permissions;
        this.journalService.dispatchStartSession();

        if (this.loggedUser) {
          if (this.loggedUser.lang) {
            if (debugLang !== 'labels') {
              const lang = debugLang || this.loggedUser.lang;
              this.translateService.use(lang);
              localStorage.setItem('backofficelanguage', lang);
            }
          }

          // if we haven't selected a clientId, use the first one
          if (Array.isArray(this.loggedUser.clientIds)) {
            if (!this.loggedUser.selectedClientId) {
              this.loggedUser.selectedClientId = this.loggedUser.clientIds[0];
            }
            // clientId is what is used all over the app
            this.loggedUser.clientId = this.loggedUser.selectedClientId;
          }
        }

        return this.loggedUser;
      }),
      catchError((err) => {
        this.reset();
        return this.handleObservableError(err);
      })
    );
  }

  getNavigationRoutes(): any[] {
    return navigationMenu;
  }

  async validateToken(credentials?: string): Promise<boolean> {
    const sessionToken = this.jwtService.getSessionToken();
    if (!sessionToken) {
      this.uiService.setLoginEnvironment(credentials);
      return false;
    }
    const apiKey = this.jwtService.getApiKey();
    if (!apiKey || !this.loggedUser) {
      await this.authService.requestApiKey();
    }
    return true;
  }

  canExecute(action: string) {
    if (!action) {
      return true;
    }

    if (this.loggedUser) {
      if (this.loggedUser.role === ROLES.SUPER_ADMIN) {
        return true;
      }

      const contextPermissions = this.contextService.getContextPermissions();
      if (UtilsService.hasDeepValue(contextPermissions, action)) {
        return this.hasLoggedUserPermissions(contextPermissions, action);
      }

      return this.hasLoggedUserPermissions(this.permissions, action);
    }
    return false;
  }

  getRoleData(roleName): Role {
    if (roleName && this.userRoles) {
      return this.userRoles.find((aRole) => aRole.name === roleName);
    }
    return null;
  }

  getRoles(role) {
    if (this.userRoles) {
      return this.userRoles
        .filter((aRole) => !role || aRole.name === role || aRole.group === role)
        .map((aRole) => aRole.name);
    }
    return [];
  }

  getScopeRoles(scope) {
    if (scope && this.userRoles) {
      return this.userRoles.filter((aRole) => aRole.scope === scope || aRole.group === scope);
    }
    return [];
  }

  getRolesOptions(
    onlyAssignable?: boolean,
    includeDemos?: boolean,
    excludeStudents?: boolean,
    userRoleName?: string
  ): Array<{ value: string; key: string }> {
    if (this.loggedUser && this.userRoles && userRoleName !== ROLES.SUPER_ADMIN) {
      const loggedUserRole = this.loggedUser.roleOptions;
      const userRoleOpt = this.getRoleData(userRoleName);
      let rolesOptions: Array<{ key: string; value: string }> = [];

      if (onlyAssignable && userRoleOpt && userRoleOpt.priority <= loggedUserRole.priority) {
        rolesOptions.push({ key: userRoleOpt.name, value: 'agents.roles.' + userRoleOpt.name });
      } else {
        rolesOptions = this.userRoles
          .filter((role) => !excludeStudents || role.name !== 'student')
          .filter((role) => !onlyAssignable || loggedUserRole.priority <= role.priority)
          .filter((role) => !userRoleOpt || role.scope === userRoleOpt.scope)
          .filter((role) => !userRoleOpt || (role.scope === 'partner' ? role.name === userRoleOpt.name : !!role))
          .map((role) => ({ key: role.name, value: 'agents.roles.' + role.name }));
      }

      if (includeDemos) {
        rolesOptions.push({ key: ROLES.DEMO, value: `agents.roles.${ROLES.DEMO}` });
      }
      return rolesOptions.map((option) => ({ ...option, value: this.translateService.instant(option.value) }));
    }
    return [{ key: userRoleName, value: userRoleName }];
  }

  changeSelectedClient(clientId, forcePath?: string) {
    if (this.loggedUser && this.loggedUser.roleOptions.scope === 'client') {
      return this.agentsService.changeClient(this.loggedUser.id, clientId).then(() => {
        this.refreshApiToken(this.loggedUser.email, clientId).then((data) => {
          this.jwtService.saveSessionToken(data.token);
          if (forcePath) {
            // eslint-disable-next-line
            window.location.href = forcePath;
          } else {
            location.reload();
          }
        });
      });
    }
  }

  getFixedClientId(): string {
    return this.loggedUser && this.loggedUser.roleOptions.scope === 'client' ? this.loggedUser.selectedClientId : null;
  }

  getLang(): string {
    return this.loggedUser ? this.loggedUser.lang : 'en';
  }

  getFixedPartnerId(): string {
    return this.loggedUser && this.loggedUser.roleOptions.scope === 'partner' ? this.loggedUser.distributorId : null;
  }

  /**
   * Refresh the session token for the API
   * @param loggedUserEmail
   * @param clientId
   */
  public refreshApiToken(loggedUserEmail?: string, clientId?: string): Promise<any> {
    return this.authService.requestApiKey(loggedUserEmail, clientId);
  }

  /**
   * Check if the logged user role has permissions
   * @param permissions
   * @param action
   * @private
   */
  private hasLoggedUserPermissions(permissions: any, action: string): boolean {
    const allowedRoles: Array<string> | any = UtilsService.getDeepValue(permissions, action);
    return !!allowedRoles && (allowedRoles === '*' || allowedRoles.includes(this.loggedUser.role));
  }
}
